// Contenu bilingue FR/DE de la FAQ.
// Ce fichier est le seul endroit à éditer pour mettre à jour les textes.
// Format supporté : Markdown (## titres, - listes, **gras**, > encadrés)

export const faqContent = {
  'getting-started': [
    {
      id: 'gs-1',
      question_fr: 'Comment créer mon premier exercice fiscal ?',
      question_de: 'Wie erstelle ich mein erstes Geschäftsjahr?',
      answer_fr: `## Créer un exercice fiscal

Accédez à **Paramètres > Exercices** depuis le menu principal.

- Saisissez l'année de l'exercice (ex : 2024)
- Définissez la période de début et de fin
- Validez pour activer la saisie de données`,
      answer_de: `## Geschäftsjahr erstellen

Navigieren Sie zu **Einstellungen > Geschäftsjahre** im Hauptmenü.

- Geben Sie das Jahr des Geschäftsjahres ein (z.B. 2024)
- Definieren Sie Start- und Enddatum
- Bestätigen Sie, um die Dateneingabe zu aktivieren`
    },
    {
      id: 'gs-2',
      question_fr: 'Comment saisir ma première activité ?',
      question_de: 'Wie gebe ich meine erste Aktivität ein?',
      answer_fr: `## Saisir une activité

Depuis le menu **Saisie de données**, vous pouvez entrer une activité de deux façons.

### Par navigation guidée

1. Sélectionnez le **scope** (1, 2 ou 3) dans les onglets de la page **Saisie de données**
2. Choisissez la bonne **catégorie** (ex : Énergie, Transport, Achats…)
3. Sélectionnez la **sous-catégorie** correspondant à votre activité
4. Choisissez le **facteur d'émission** et son unité (kWh, litres, km, kg…)
5. Renseignez la **quantité** consommée sur la période
6. Validez — l'émission est ajoutée au bilan de l'exercice

### Par recherche globale

Si vous connaissez déjà le facteur, utilisez la **barre de recherche** en haut de la page de saisie : saisissez un mot-clé (ex : *fioul*, *électricité*, *avion*) pour accéder directement au facteur sans passer par la navigation par catégorie.

> **Conseil** : pour les premières saisies, préférez la navigation guidée afin de vous familiariser avec la structure des scopes et catégories.`,
      answer_de: `## Eine Aktivität erfassen

Im Menü **Dateneingabe** können Sie eine Aktivität auf zwei Arten erfassen.

### Über die geführte Navigation

1. Wählen Sie den **Scope** (1, 2 oder 3) in den Tabs der Seite **Dateneingabe**
2. Wählen Sie die richtige **Kategorie** (z.B. Energie, Transport, Einkauf…)
3. Wählen Sie die passende **Unterkategorie**
4. Wählen Sie den **Emissionsfaktor** und die Einheit (kWh, Liter, km, kg…)
5. Geben Sie die verbrauchte **Menge** im Zeitraum ein
6. Bestätigen Sie — die Emission wird der Bilanz des Geschäftsjahres hinzugefügt

### Über die globale Suche

Wenn Sie den Faktor bereits kennen, nutzen Sie die **Suchleiste** oben auf der Eingabeseite : Geben Sie ein Stichwort ein (z.B. *Heizöl*, *Strom*, *Flugzeug*), um direkt zum Faktor zu gelangen — ohne die Kategorienavigation zu durchlaufen.

> **Tipp** : Bei den ersten Eingaben empfiehlt sich die geführte Navigation, um sich mit der Struktur der Scopes und Kategorien vertraut zu machen.`
    },
{
      id: 'gs-3',
      question_fr: "Qu'est-ce qu'un périmètre de bilan carbone ?",
      question_de: 'Was ist ein CO2-Bilanzperimeter?',
      answer_fr: `## Périmètre de bilan carbone

Le périmètre définit **ce que vous incluez dans votre bilan** — autrement dit, de quelles activités et entités vous êtes responsable.

## Périmètre organisationnel
Il répond à la question : **quelles entités de mon organisation sont concernées ?**

- Une filiale, un site de production, un bureau régional
- En général, on inclut toutes les entités sur lesquelles on a un contrôle opérationnel ou financier

## Périmètre opérationnel
Il répond à la question : **quels types d'émissions je comptabilise ?**

- **Scope 1** — ce que vous brûlez ou émettez directement (fioul, gaz, flotte de véhicules)
- **Scope 2** — l'énergie que vous achetez (électricité, chaleur)
- **Scope 3** — tout le reste de votre chaîne de valeur (fournisseurs, transport, produits vendus)

## Notion de matérialité
Il n'est pas nécessaire de tout comptabiliser. Le principe de matérialité vous permet d'**exclure les sources d'émissions négligeables** — celles qui représentent une part infime de votre bilan total et dont l'impact sur le résultat final serait insignifiant.

> Plus le périmètre est large, plus le bilan est représentatif de votre impact réel. L'objectif est d'être exhaustif sur ce qui compte, pas sur ce qui est anecdotique.`,
      answer_de: `## CO2-Bilanzperimeter

Der Perimeter legt fest, **was Sie in Ihre Bilanz einbeziehen** — also für welche Aktivitäten und Einheiten Sie verantwortlich sind.

## Organisatorischer Perimeter
Er beantwortet die Frage: **Welche Teile meiner Organisation sind betroffen?**

- Eine Tochtergesellschaft, ein Produktionsstandort, ein Regionalbüro
- In der Regel werden alle Einheiten einbezogen, über die man operative oder finanzielle Kontrolle hat

## Operativer Perimeter
Er beantwortet die Frage: **Welche Emissionsarten erfasse ich?**

- **Scope 1** — was Sie direkt verbrennen oder emittieren (Heizöl, Gas, Fahrzeugflotte)
- **Scope 2** — zugekaufte Energie (Strom, Wärme)
- **Scope 3** — der Rest Ihrer Wertschöpfungskette (Lieferanten, Transport, verkaufte Produkte)

## Wesentlichkeitsprinzip
Es ist nicht notwendig, alles zu erfassen. Das Wesentlichkeitsprinzip erlaubt es Ihnen, **vernachlässigbare Emissionsquellen auszuschließen** — solche, die einen verschwindend geringen Anteil an Ihrer Gesamtbilanz ausmachen und deren Einfluss auf das Gesamtergebnis insignifikant wäre.

> Je breiter der Perimeter, desto repräsentativer ist die Bilanz für Ihren tatsächlichen Impact. Das Ziel ist Vollständigkeit bei dem, was zählt — nicht bei dem, was anekdotisch ist.`
    },
  ],

  'dashboard': [
    {
      id: 'db-1',
      question_fr: 'Comment interpréter les KPIs affichés ?',
      question_de: 'Wie interpretiere ich die angezeigten KPIs?',
      answer_fr: `## Indicateurs clés (KPIs)

Le tableau de bord affiche trois indicateurs principaux :

- **Émissions totales (tCO₂e)** : la somme de toutes les émissions comptabilisées sur l'exercice, tous scopes confondus
- **Émissions par kCHF** : vos émissions rapportées à votre chiffre d'affaires — cet indicateur d'intensité permet de comparer votre performance carbone indépendamment de la taille de l'entreprise
- **Variation N-1** : l'évolution de vos émissions totales par rapport à l'exercice précédent, exprimée en pourcentage



## Émissions par Scope

Ce graphique en barres montre la répartition de vos émissions entre les trois scopes GHG Protocol. Il permet d'identifier **où se concentre l'essentiel de votre impact** — souvent le Scope 3 pour les entreprises de services. En cliquant dessus, vous pourrez zoomer sur un niveau de détails plus précis.

## Top 7 sous-catégories

Ce classement met en évidence les **7 sources d'émissions les plus significatives** de votre bilan. C'est un outil de priorisation : les premières lignes indiquent là où des actions de réduction auraient le plus d'effet.

## Évolution des émissions

Ce graphique retrace la **trajectoire de vos émissions dans le temps**, exercice par exercice. Il permet de visualiser si vos efforts de réduction portent leurs fruits et de comparer votre trajectoire réelle avec vos objectifs fixés.`,
      answer_de: `## Schlüsselindikatoren (KPIs)

Das Dashboard zeigt drei Hauptindikatoren:

- **Gesamtemissionen (tCO₂e)** : die Summe aller erfassten Emissionen des Geschäftsjahres, über alle Scopes hinweg
- **Emissionen pro kCHF** : Ihre Emissionen im Verhältnis zum Umsatz — dieser Intensitätsindikator ermöglicht den Vergleich der CO₂-Leistung unabhängig von der Unternehmensgröße
- **Variation N-1** : die Entwicklung Ihrer Gesamtemissionen gegenüber dem Vorjahr, ausgedrückt in Prozent



## Emissionen nach Scope

Dieses Balkendiagramm zeigt die Verteilung Ihrer Emissionen auf die drei GHG-Protocol-Scopes. Es hilft zu erkennen, **wo sich der Grossteil Ihres Impacts konzentriert** — bei Dienstleistungsunternehmen oft Scope 3. Durch Klicken darauf können Sie in eine detailliertere Ansicht zoomen.

## Top 7 Unterkategorien

Dieses Ranking hebt die **7 bedeutendsten Emissionsquellen** Ihrer Bilanz hervor. Es ist ein Priorisierungswerkzeug: Die obersten Einträge zeigen, wo Reduktionsmassnahmen die grösste Wirkung hätten.

## Emissionsentwicklung

Dieses Diagramm zeigt die **Emissionstrajektorie im Zeitverlauf**, Geschäftsjahr für Geschäftsjahr. Es ermöglicht zu erkennen, ob Ihre Reduktionsbemühungen Früchte tragen, und Ihre tatsächliche Trajektorie mit Ihren gesetzten Zielen zu vergleichen.`
    },
    {
      id: 'db-2',
      question_fr: 'Comment fonctionnent les scénarios ?',
      question_de: 'Wie funktionieren Szenarien?',
      answer_fr: `## Scénarios : projeter vos émissions futures

Les scénarios permettent de simuler des projections *what-if* (« et si on remplaçait notre flotte thermique par de l'électrique en 2027 ? ») **sans toucher aux données réelles** de vos exercices.

### Création par duplication

Un scénario naît toujours d'un exercice **réel existant** qui sert de point de départ.

1. Depuis la page Exercices, cliquez sur **Dupliquer** un exercice réel
2. Choisissez le mode **Scénario** dans la modale
3. Sélectionnez un conteneur de scénario existant ou créez-en un nouveau (ex : « Plan décarbonation 2030 »)
4. Choisissez l'année cible (ex : duplication de 2024 en projection 2027)

Toutes les **activités** de l'exercice source sont copiées et ajustées à la nouvelle période. Les facteurs d'émission et les produits restent partagés.

> **Notion clé** : un conteneur de scénario peut regrouper **plusieurs années** (2025, 2026, 2027…), permettant de tracer une trajectoire complète de projection.

### Édition indépendante

Une fois créé, le scénario est totalement modifiable comme un exercice normal — quantités, activités, contexte. Aucun impact sur l'exercice réel d'origine.

### Isolation des données réelles

Les scénarios sont **automatiquement exclus** :

- Des KPIs et de la trajectoire officielle du tableau de bord
- Du calcul des objectifs SBTi
- Des exports et rapports

Ils n'apparaissent que lorsqu'ils sont explicitement sélectionnés.

### Superposition sur le tableau de bord

Un sélecteur de scénario permet de visualiser la trajectoire projetée par-dessus la réalité :

- Les exercices réels apparaissent en **barres pleines** (scope 1, 2, 3 empilés)
- Les années du scénario apparaissent en **barres hachurées et semi-transparentes** par-dessus

On voit immédiatement où la trajectoire projetée diverge de la trajectoire réelle.

### Cas d'usage typiques

- Tester l'impact d'un changement de fournisseur d'énergie
- Projeter une trajectoire de réduction sur 5-10 ans pour valider un objectif SBTi
- Comparer plusieurs plans de décarbonation (chaque plan = un conteneur)
- Préparer un budget carbone avant validation`,
      answer_de: `## Szenarien : Ihre zukünftigen Emissionen projizieren

Szenarien ermöglichen *What-if*-Projektionen (« Was wäre, wenn wir unsere Verbrennerflotte 2027 durch Elektrofahrzeuge ersetzen ? ») **ohne die realen Daten Ihrer Geschäftsjahre zu verändern**.

### Erstellung durch Duplizierung

Ein Szenario entsteht immer aus einem **bestehenden realen Geschäftsjahr** als Ausgangspunkt.

1. Klicken Sie auf der Geschäftsjahre-Seite bei einem realen Geschäftsjahr auf **Duplizieren**
2. Wählen Sie im Dialog den Modus **Szenario**
3. Wählen Sie einen bestehenden Szenario-Container oder erstellen Sie einen neuen (z.B. « Dekarbonisierungsplan 2030 »)
4. Wählen Sie das Zieljahr (z.B. Duplizierung von 2024 als Projektion 2027)

Alle **Aktivitäten** des Quell-Geschäftsjahres werden kopiert und an den neuen Zeitraum angepasst. Emissionsfaktoren und Produkte bleiben gemeinsam genutzt.

> **Schlüsselkonzept** : Ein Szenario-Container kann **mehrere Jahre** umfassen (2025, 2026, 2027…), um eine vollständige Projektionstrajektorie abzubilden.

### Unabhängige Bearbeitung

Einmal erstellt, lässt sich ein Szenario wie ein normales Geschäftsjahr vollständig bearbeiten — Mengen, Aktivitäten, Kontext. Keinerlei Auswirkungen auf das ursprüngliche reale Geschäftsjahr.

### Isolation der realen Daten

Szenarien werden **automatisch ausgeschlossen** :

- Aus den KPIs und der offiziellen Trajektorie des Dashboards
- Aus der SBTi-Zielberechnung
- Aus Exporten und Berichten

Sie erscheinen nur, wenn sie explizit ausgewählt werden.

### Überlagerung im Dashboard

Eine Szenario-Auswahl ermöglicht die Visualisierung der projizierten Trajektorie über der Realität :

- Reale Geschäftsjahre erscheinen als **gefüllte Balken** (Scope 1, 2, 3 gestapelt)
- Szenario-Jahre erscheinen als **schraffierte, halbtransparente Balken** darüber

So sehen Sie sofort, wo die projizierte Trajektorie von der realen abweicht.

### Typische Anwendungsfälle

- Den Impact eines Energieversorgerwechsels testen
- Eine Reduktionstrajektorie über 5-10 Jahre projizieren, um ein SBTi-Ziel zu validieren
- Mehrere Dekarbonisierungspläne vergleichen (jeder Plan = ein Container)
- Ein CO₂-Budget vor Validierung vorbereiten`
    },
    {
      id: 'db-3',
      question_fr: 'Comment définir un objectif SBTi ?',
      question_de: 'Wie definiere ich ein SBTi-Ziel?',
      answer_fr: `## Objectif basé sur la méthodologie SBTi

CarbonScope vous permet de définir des trajectoires de réduction **inspirées de la méthodologie Science Based Targets (SBTi)**, compatible avec les accords de Paris et visant à limiter le réchauffement à 1,5°C ou bien en dessous de 2°C.

> **Important** : les objectifs configurés dans CarbonScope suivent la méthodologie SBTi, mais ne constituent pas des engagements SBTi officiels. Une validation formelle auprès de l'initiative SBTi est nécessaire pour pouvoir communiquer publiquement sur un engagement reconnu.

## Comment configurer votre objectif

Accédez à **Tableau de bord > Objectifs** pour définir votre trajectoire :

- **Année de référence** : l'exercice à partir duquel vous mesurez vos progrès (généralement votre première année de bilan)
- **Année cible** : l'horizon que vous vous fixez (ex : 2030 ou 2050)
- **Pourcentage de réduction** : la baisse d'émissions visée par rapport à l'année de référence

## Visualisation

Une fois configuré, votre objectif apparaît en superposition sur le graphique d'évolution des émissions, sous forme d'une ligne de trajectoire cible. Cela vous permet de comparer en un coup d'œil votre trajectoire réelle avec votre engagement.`,
      answer_de: `## Ziel basierend auf der SBTi-Methodik

CarbonScope ermöglicht es Ihnen, Reduktionsziele zu definieren, die **auf der Methodik der Science Based Targets (SBTi) basieren** und mit dem Pariser Abkommen vereinbar sind — d.h. sie zielen darauf ab, die Erwärmung auf 1,5°C oder deutlich unter 2°C zu begrenzen.

> **Wichtig** : Die in CarbonScope konfigurierten Ziele folgen der SBTi-Methodik, stellen jedoch keine offiziellen SBTi-Verpflichtungen dar. Eine formelle Validierung durch die SBTi-Initiative ist erforderlich, um öffentlich über eine anerkannte Verpflichtung kommunizieren zu können.

## Konfiguration Ihres Ziels

Navigieren Sie zu **Dashboard > Ziele**, um Ihre Trajektorie zu definieren:

- **Basisjahr** : das Geschäftsjahr, ab dem Sie Ihren Fortschritt messen (in der Regel Ihr erstes Bilanzjahr)
- **Zieljahr** : der Zeithorizont, den Sie sich setzen (z.B. 2030 oder 2050)
- **Reduktionsprozentsatz** : die angestrebte Emissionsreduktion gegenüber dem Basisjahr

## Visualisierung

Nach der Konfiguration erscheint Ihr Ziel als überlagerte Zieltrajektorielinie im Emissionsentwicklungsdiagramm. So können Sie auf einen Blick Ihre tatsächliche Trajektorie mit Ihrem Engagement vergleichen.`
    },
  ],

  'scopes': [
{
      id: 'sc-1',
      question_fr: 'Quelle est la différence entre Scope 1, 2 et 3 ?',
      question_de: 'Was ist der Unterschied zwischen Scope 1, 2 und 3?',
      answer_fr: `## Les trois scopes du GHG Protocol

Le GHG Protocol est le standard international de référence pour la comptabilité carbone. Il organise les émissions en trois catégories appelées "scopes", selon leur origine et leur lien avec votre activité.

## Scope 1 — Émissions directes
Ce sont les émissions que vous produisez **directement** sur vos sites ou avec vos équipements :

- Combustion de fioul, gaz naturel ou propane pour le chauffage
- Carburant consommé par votre flotte de véhicules
- Émissions de procédés industriels sur site

## Scope 2 — Émissions indirectes liées à l'énergie
Ce sont les émissions générées par la **production de l'énergie que vous achetez** :

- Électricité consommée dans vos locaux
- Chaleur ou vapeur achetée à un réseau

> Ces émissions se produisent chez votre fournisseur d'énergie, pas chez vous — mais elles sont liées à votre consommation.

## Scope 3 — Autres émissions indirectes
Ce sont toutes les émissions liées à votre activité mais **hors de votre contrôle direct** :

- Achats de biens et services (amont)
- Transport et logistique
- Déplacements professionnels et domicile-travail
- Utilisation et fin de vie de vos produits vendus (aval)

> Pour la majorité des entreprises, le Scope 3 représente plus de 70% des émissions totales. C'est souvent là que se trouvent les leviers de réduction les plus importants.`,
      answer_de: `## Die drei Scopes des GHG-Protokolls

Das GHG-Protokoll ist der internationale Referenzstandard für die CO₂-Bilanzierung. Es unterteilt Emissionen in drei Kategorien, sogenannte "Scopes", je nach ihrer Herkunft und ihrem Bezug zu Ihrer Aktivität.

## Scope 1 — Direkte Emissionen
Dies sind Emissionen, die Sie **direkt** an Ihren Standorten oder mit Ihren Anlagen erzeugen:

- Verbrennung von Heizöl, Erdgas oder Propan zur Heizung
- Kraftstoffverbrauch Ihrer Fahrzeugflotte
- Industrielle Prozessemissionen vor Ort

## Scope 2 — Indirekte Emissionen aus Energie
Dies sind Emissionen, die bei der **Erzeugung der von Ihnen zugekauften Energie** entstehen:

- In Ihren Räumlichkeiten verbrauchter Strom
- Zugekaufte Wärme oder Dampf aus einem Netz

> Diese Emissionen entstehen bei Ihrem Energielieferanten, nicht bei Ihnen — sind aber mit Ihrem Verbrauch verbunden.

## Scope 3 — Sonstige indirekte Emissionen
Dies sind alle Emissionen, die mit Ihrer Aktivität verbunden sind, aber **ausserhalb Ihrer direkten Kontrolle** liegen:

- Einkauf von Waren und Dienstleistungen (vorgelagert)
- Transport und Logistik
- Geschäftsreisen und Pendelverkehr
- Nutzung und Entsorgung Ihrer verkauften Produkte (nachgelagert)

> Für die meisten Unternehmen macht Scope 3 mehr als 70% der Gesamtemissionen aus. Hier liegen oft die bedeutendsten Reduktionshebel.`
    },
    {
      id: 'sc-2',
      question_fr: 'Description des catégories GHG Protocol utilisées dans CarbonScope',
      question_de: 'Beschreibung der im CarbonScope verwendeten GHG-Protokoll-Kategorien',
      answer_fr: `## Les catégories du GHG Protocol utilisées dans CarbonScope

Le GHG Protocol structure les émissions selon trois scopes, chacun découpé en catégories spécifiques. CarbonScope expose les catégories les plus couramment pertinentes pour les entreprises suisses. Voici un guide rapide pour vous aider à choisir où saisir chaque activité.

## Scope 1 — Émissions directes

- **Combustion fixe** : combustion sur site dans des installations stationnaires.
  *Exemple : chaudière à gaz pour le chauffage des bureaux, four industriel au mazout.*

- **Combustion mobile** : combustion dans des véhicules ou engins que vous possédez ou contrôlez.
  *Exemple : voitures de fonction diesel, camion de livraison de votre flotte.*

- **Émissions de procédés** : émissions issues de réactions chimiques dans vos procédés industriels (hors combustion).
  *Exemple : décarbonatation du calcaire dans une cimenterie, fermentation dans une brasserie.*

- **Émissions fugitives** : fuites involontaires de gaz à effet de serre par vos équipements.
  *Exemple : fluides frigorigènes qui s'échappent d'une climatisation ou d'un groupe froid.*

## Scope 2 — Énergie achetée

- **Électricité** : électricité achetée à un fournisseur du réseau pour votre consommation propre.
  *Exemple : facture d'électricité de vos bureaux ou de votre site de production.*

- **Chaleur et vapeur** : chaleur ou vapeur achetée à un réseau externe (CAD, vapeur industrielle).
  *Exemple : raccordement à un réseau de chauffage à distance alimenté par une centrale thermique.*

- **Réseau de froid** : froid acheté à un réseau externe pour climatisation ou réfrigération.
  *Exemple : raccordement à un réseau de froid urbain (rare en Suisse mais existant à Genève, par exemple).*

## Scope 3 amont

- **3.1 Biens et services achetés** : émissions liées à la production des biens et services que vous achetez (hors immobilisations).
  *Exemple : achat de papier, prestations informatiques, fournitures de bureau.*

- **3.2 Biens d'équipement** : émissions liées à la fabrication des biens durables que vous achetez (immobilisations).
  *Exemple : achat d'une nouvelle machine de production, d'un véhicule, d'un serveur informatique.*

- **3.3 Émissions amont liées aux combustibles et à l'énergie** : émissions de l'extraction, du raffinage et du transport des combustibles et de l'énergie consommée — hors combustion (qui est en Scope 1) ou production (qui est en Scope 2).
  *Exemple : extraction et raffinage du fioul que vous brûlez, pertes en ligne de votre électricité.*

- **3.4 Transport et distribution amont** : émissions du transport des biens depuis vos fournisseurs jusqu'à vous (hors transport déjà inclus dans le prix d'achat).
  *Exemple : camion qui livre vos matières premières, fret aérien depuis l'Asie.*

- **3.5 Déchets générés en exploitation** : émissions du traitement des déchets que vous produisez (hors déchets de produits vendus, qui sont rattachés aux produits).
  *Exemple : enlèvement et incinération des déchets de bureau, traitement des eaux usées industrielles.*

- **3.6 Déplacements professionnels** : émissions des voyages d'affaires effectués par vos collaborateurs.
  *Exemple : vol pour un congrès, train pour un client, nuitées d'hôtel.*

- **3.7 Déplacements domicile-travail** : émissions des trajets quotidiens de vos collaborateurs entre leur domicile et le bureau.
  *Exemple : enquête mobilité de vos employés, télétravail inclus.*

- **3.8 Actifs en leasing amont** : émissions des actifs que vous louez à un tiers (et que vous utilisez), si leurs émissions ne sont pas déjà incluses en Scope 1 ou 2.
  *Exemple : bureaux loués dont vous ne payez pas l'électricité directement, voiture en leasing si non comptée en Scope 1.*

## Scope 3 aval

### Produits vendus (catégories 3.10, 3.11, 3.12 regroupées)

CarbonScope rassemble en une seule entrée **« Produits vendus »** les trois catégories standard liées à vos produits, car elles sont calculées ensemble à partir de la fiche produit que vous créez (voir question dédiée aux fiches produits). Les trois impacts couverts :

- **3.10 Transformation des produits vendus** : émissions de la transformation de vos produits vendus par un tiers avant leur usage final.
  *Exemple : vous vendez de l'acier brut qui est ensuite usiné par un client industriel.*

- **3.11 Utilisation des produits vendus** : émissions liées à l'usage de vos produits par vos clients sur leur durée de vie.
  *Exemple : électricité consommée par les machines à café que vous fabriquez et vendez, carburant brûlé par les voitures que vous vendez.*

- **3.12 Fin de vie des produits vendus** : émissions du traitement de vos produits vendus une fois en fin de vie.
  *Exemple : incinération, recyclage ou mise en décharge de vos emballages, équipements vendus.*

### Autres catégories aval

- **3.9 Transport et distribution aval** : émissions du transport de vos produits vendus jusqu'à vos clients (hors transport déjà payé par vos clients).
  *Exemple : livraison de vos produits depuis votre entrepôt jusqu'au client final.*

- **3.13 Actifs en leasing aval** : émissions des actifs que vous possédez et louez à des tiers (côté loueur).
  *Exemple : si vous êtes propriétaire bailleur d'un bâtiment, les émissions de vos locataires.*

- **3.14 Franchises** : émissions des activités menées sous votre marque par des franchisés.
  *Exemple : émissions des restaurants franchisés sous votre enseigne.*

- **3.15 Investissements** : émissions des entreprises dans lesquelles vous avez des participations financières.
  *Exemple : pour une banque, émissions liées à son portefeuille de prêts ; pour un fonds, émissions de ses participations.*

> **En pratique** : vous n'avez probablement pas besoin de toutes les 15 catégories Scope 3. Le principe de matérialité (voir question précédente) vous autorise à concentrer vos efforts sur les catégories qui pèsent vraiment dans votre bilan. Pour la plupart des entreprises de services, ce sont 3.1 (achats), 3.6 (déplacements pro) et 3.7 (domicile-travail) qui dominent. Pour les industriels, ajoutez 3.4 (transport amont), les Produits vendus (3.10/3.11/3.12).`,
      answer_de: `## Die im CarbonScope verwendeten Kategorien des GHG-Protokolls

Das GHG-Protokoll strukturiert Emissionen in drei Scopes, die jeweils in spezifische Kategorien unterteilt sind. CarbonScope bildet diejenigen Kategorien ab, die für Schweizer Unternehmen am häufigsten relevant sind. Hier ein kurzer Leitfaden, um Ihnen bei der Zuordnung Ihrer Aktivitäten zu helfen.

## Scope 1 — Direkte Emissionen

- **Stationäre Verbrennung** : Verbrennung in stationären Anlagen vor Ort.
  *Beispiel : Gasheizung für Bürogebäude, industrieller Heizölofen.*

- **Mobile Verbrennung** : Verbrennung in Fahrzeugen oder Maschinen, die Sie besitzen oder kontrollieren.
  *Beispiel : Diesel-Geschäftswagen, firmeneigener Lieferwagen.*

- **Prozessemissionen** : Emissionen aus chemischen Reaktionen in Ihren industriellen Prozessen (ohne Verbrennung).
  *Beispiel : Entsäuerung von Kalkstein in der Zementherstellung, Gärung in einer Brauerei.*

- **Flüchtige Emissionen** : unbeabsichtigte Lecks von Treibhausgasen aus Ihren Anlagen.
  *Beispiel : Kältemittel, die aus einer Klimaanlage oder Kälteanlage entweichen.*

## Scope 2 — Indirekte Energie

- **Elektrizität** : aus dem Netz zugekaufte Elektrizität für den eigenen Verbrauch.
  *Beispiel : Stromrechnung Ihrer Büros oder Ihres Produktionsstandorts.*

- **Wärme und Dampf** : aus einem externen Netz zugekaufte Wärme oder Dampf (Fernwärme, industrieller Dampf).
  *Beispiel : Anschluss an ein Fernwärmenetz, das von einem Heizkraftwerk gespeist wird.*

- **Kühlung** : aus einem externen Netz zugekaufte Kälte für Klimatisierung oder Kühlung.
  *Beispiel : Anschluss an ein städtisches Fernkältenetz (in der Schweiz selten, aber z.B. in Genf vorhanden).*

## Scope 3 vorgelagert

- **3.1 Gekaufte Waren und Dienstleistungen** : Emissionen aus der Produktion der von Ihnen eingekauften Güter und Dienstleistungen (ohne Investitionsgüter).
  *Beispiel : Einkauf von Papier, IT-Dienstleistungen, Büromaterial.*

- **3.2 Investitionsgüter** : Emissionen aus der Herstellung der von Ihnen gekauften langlebigen Güter (Anlagevermögen).
  *Beispiel : Anschaffung einer neuen Produktionsmaschine, eines Fahrzeugs, eines IT-Servers.*

- **3.3 Brennstoff- und energiebezogene Aktivitäten** : Emissionen aus Gewinnung, Raffination und Transport der von Ihnen verbrauchten Brennstoffe und Energie — ohne Verbrennung (Scope 1) oder Produktion (Scope 2).
  *Beispiel : Förderung und Raffination des von Ihnen verbrannten Heizöls, Netzverluste Ihres Stroms.*

- **3.4 Vorgelagerter Transport und Verteilung** : Emissionen aus dem Transport von Gütern von Ihren Lieferanten zu Ihnen (sofern nicht bereits im Einkaufspreis enthalten).
  *Beispiel : LKW, der Ihre Rohstoffe liefert; Luftfracht aus Asien.*

- **3.5 Abfälle aus dem Betrieb** : Emissionen aus der Behandlung der von Ihnen erzeugten Abfälle (ohne Abfälle aus verkauften Produkten, die den Produkten zugeordnet sind).
  *Beispiel : Entsorgung und Verbrennung von Büroabfällen, Behandlung von Industrieabwasser.*

- **3.6 Geschäftsreisen** : Emissionen aus den Geschäftsreisen Ihrer Mitarbeitenden.
  *Beispiel : Flug zu einer Konferenz, Zugfahrt zu einem Kunden, Hotelübernachtungen.*

- **3.7 Pendeln der Mitarbeiter** : Emissionen aus den täglichen Arbeitswegen Ihrer Mitarbeitenden zwischen Wohnort und Büro.
  *Beispiel : Mobilitätsumfrage Ihrer Mitarbeitenden, Homeoffice inklusive.*

- **3.8 Vorgelagerte Leasinggüter** : Emissionen aus Vermögenswerten, die Sie von Dritten leasen (und nutzen), sofern diese nicht bereits in Scope 1 oder 2 enthalten sind.
  *Beispiel : gemietete Büros, deren Strom Sie nicht direkt zahlen; Leasingfahrzeug, sofern nicht in Scope 1 erfasst.*

## Scope 3 nachgelagert

### Verkaufte Produkte (Kategorien 3.10, 3.11, 3.12 zusammengefasst)

CarbonScope fasst die drei produktbezogenen Standardkategorien in einem einzigen Eintrag **„Verkaufte Produkte"** zusammen, da sie gemeinsam aus der von Ihnen erstellten Produktkarte berechnet werden (siehe spezielle Frage zu Produktkarten). Die drei abgedeckten Auswirkungen :

- **3.10 Verarbeitung verkaufter Produkte** : Emissionen aus der Weiterverarbeitung Ihrer verkauften Produkte durch Dritte vor der Endnutzung.
  *Beispiel : Sie verkaufen Rohstahl, der anschliessend von einem Industriekunden weiterverarbeitet wird.*

- **3.11 Nutzung verkaufter Produkte** : Emissionen aus der Nutzung Ihrer Produkte durch Ihre Kunden über deren Lebensdauer.
  *Beispiel : Strom, der von Kaffeemaschinen verbraucht wird, die Sie herstellen und verkaufen; Kraftstoff, der von verkauften Fahrzeugen verbrannt wird.*

- **3.12 Lebensende verkaufter Produkte** : Emissionen aus der Behandlung Ihrer verkauften Produkte am Ende ihrer Lebensdauer.
  *Beispiel : Verbrennung, Recycling oder Deponierung Ihrer Verpackungen oder verkauften Geräte.*

### Weitere nachgelagerte Kategorien

- **3.9 Nachgelagerter Transport und Verteilung** : Emissionen aus dem Transport Ihrer verkauften Produkte zu Ihren Kunden (sofern nicht bereits vom Kunden bezahlt).
  *Beispiel : Lieferung Ihrer Produkte vom Lager bis zum Endkunden.*

- **3.13 Nachgelagerte Leasinggüter** : Emissionen aus Vermögenswerten, die Sie besitzen und an Dritte vermieten (Vermieterseite).
  *Beispiel : Wenn Sie Eigentümer und Vermieter eines Gebäudes sind, die Emissionen Ihrer Mieter.*

- **3.14 Franchise** : Emissionen aus Aktivitäten, die unter Ihrer Marke von Franchisenehmern durchgeführt werden.
  *Beispiel : Emissionen von Franchise-Restaurants unter Ihrem Markennamen.*

- **3.15 Investitionen** : Emissionen aus Unternehmen, an denen Sie finanzielle Beteiligungen halten.
  *Beispiel : Für eine Bank die Emissionen ihres Kreditportfolios; für einen Fonds die Emissionen seiner Beteiligungen.*

> **In der Praxis** : Sie benötigen wahrscheinlich nicht alle 15 Scope-3-Kategorien. Das Wesentlichkeitsprinzip (siehe vorherige Frage) erlaubt es Ihnen, sich auf die Kategorien zu konzentrieren, die wirklich in Ihrer Bilanz ins Gewicht fallen. Bei den meisten Dienstleistungsunternehmen dominieren 3.1 (Einkäufe), 3.6 (Geschäftsreisen) und 3.7 (Pendelverkehr). Bei Industrieunternehmen kommen 3.4 (vorgelagerter Transport) und die Verkauften Produkte (3.10/3.11/3.12) hinzu.`
    },
    {
      id: 'sc-3',
      question_fr: "Qu'est-ce que le dual reporting ?",
      question_de: 'Was ist Dual Reporting?',
      answer_fr: `## Dual reporting — Scope 2 market-based vs location-based

Le GHG Protocol impose depuis 2015 un **double calcul du Scope 2**, connu sous le nom de "dual reporting". Cette exigence répond à une question concrète : si vous achetez de l'électricité certifiée renouvelable, vos émissions sont-elles nulles ?

## Location-based
Le calcul se base sur le **mix électrique moyen du réseau** de votre pays ou région — indépendamment de ce que vous achetez. C'est le reflet de l'impact réel sur le réseau électrique.

## Market-based
Le calcul utilise le **facteur d'émission lié à votre contrat d'achat** — par exemple zéro si vous avez acquis des garanties d'origine (GO) ou des certificats d'énergie renouvelable (REC).

> Le dual reporting évite de masquer la réalité physique du réseau derrière des instruments financiers. Les deux valeurs doivent être publiées, ce qui garantit la transparence de votre bilan.

## Dans CarbonScope

Les facteurs d'émission de type **market-based** sont automatiquement associés à leur équivalent **location-based**. Le calcul des deux valeurs se fait en parallèle, sans saisie supplémentaire de votre part.

Depuis le tableau de bord, vous pouvez **basculer entre la vue market-based et location-based** pour comparer les deux résultats et choisir quelle perspective mettre en avant dans vos rapports.`,
      answer_de: `## Dual Reporting — Scope 2 Market-based vs. Location-based

Das GHG-Protokoll schreibt seit 2015 eine **doppelte Berechnung von Scope 2** vor, bekannt als "Dual Reporting". Diese Anforderung beantwortet eine konkrete Frage: Wenn Sie zertifizierten Ökostrom kaufen, sind Ihre Emissionen dann null?

## Location-based
Die Berechnung basiert auf dem **durchschnittlichen Strommix des Netzes** Ihres Landes oder Ihrer Region — unabhängig davon, was Sie kaufen. Dies spiegelt den tatsächlichen Einfluss auf das Stromnetz wider.

## Market-based
Die Berechnung verwendet den **Emissionsfaktor Ihres Kaufvertrags** — zum Beispiel null, wenn Sie Herkunftsnachweise (GO) oder erneuerbare Energiezertifikate (REC) erworben haben.

> Dual Reporting verhindert, dass die physische Netzrealität hinter Finanzinstrumenten verborgen wird. Beide Werte müssen veröffentlicht werden, was die Transparenz Ihrer Bilanz gewährleistet.

## In CarbonScope

**Market-based** Emissionsfaktoren werden automatisch mit ihrem **location-based** Äquivalent verknüpft. Die Berechnung beider Werte erfolgt parallel, ohne zusätzliche Eingaben Ihrerseits.

Über das Dashboard können Sie **zwischen der Market-based- und der Location-based-Ansicht wechseln**, um beide Ergebnisse zu vergleichen und zu entscheiden, welche Perspektive Sie in Ihren Berichten hervorheben möchten.`
    },
    {
      id: 'sc-4',
      question_fr: "Qu'est-ce qu'un facteur multi-impacts ?",
      question_de: 'Was ist ein Multi-Impact-Faktor?',
      answer_fr: `## Facteur multi-impacts

Un facteur multi-impacts est un facteur d'émission qui génère des émissions sur **plusieurs scopes simultanément**. C'est le cas notamment des combustibles fossiles, qui produisent à la fois des émissions directes lors de leur combustion (Scope 1) et des émissions liées à leur extraction et transport (Scope 3).

**Exemple — Fioul domestique** :
- **Scope 1** : émissions de la combustion sur votre site
- **Scope 3.3** : émissions liées à l'extraction, au raffinage et au transport du fioul jusqu'à vous

## Catégorie 3.3 — Émissions amont liées aux combustibles et à l'énergie

La catégorie 3.3 du Scope 3 couvre les **émissions indirectes liées à la production et au transport des combustibles et de l'énergie** que vous consommez — hors combustion directe.

## Calcul automatique dans CarbonScope

Dès lors qu'un facteur multi-impacts est sélectionné, CarbonScope calcule automatiquement la part Scope 3.3 sans action supplémentaire de votre part :

1. Vous saisissez la quantité de combustible consommée
2. L'application applique simultanément le coefficient Scope 1 et le coefficient Scope 3.3 correspondants
3. Les résultats apparaissent séparément dans votre bilan, correctement ventilés par scope

> Ce calcul automatique garantit que votre bilan est complet et conforme au GHG Protocol, sans risque d'oubli ou de double comptage.`,
      answer_de: `## Multi-Impact-Faktor

Ein Multi-Impact-Faktor ist ein Emissionsfaktor, der Emissionen in **mehreren Scopes gleichzeitig** erzeugt. Dies gilt insbesondere für fossile Brennstoffe, die sowohl direkte Emissionen bei der Verbrennung (Scope 1) als auch Emissionen aus ihrer Gewinnung und ihrem Transport erzeugen (Scope 3).

**Beispiel — Heizöl** :
- **Scope 1** : Emissionen aus der Verbrennung an Ihrem Standort
- **Scope 3.3** : Emissionen aus der Gewinnung, Raffination und dem Transport des Heizöls zu Ihnen

## Kategorie 3.3 — Vorgelagerte Emissionen aus Brennstoffen und Energie

Kategorie 3.3 von Scope 3 umfasst die **indirekten Emissionen aus der Erzeugung und dem Transport der von Ihnen verbrauchten Brennstoffe und Energie** — ohne direkte Verbrennung.

## Automatische Berechnung in CarbonScope

Sobald ein Multi-Impact-Faktor ausgewählt wird, berechnet CarbonScope den Scope-3.3-Anteil automatisch, ohne zusätzliche Eingaben Ihrerseits:

1. Sie erfassen die verbrauchte Brennstoffmenge
2. Die Anwendung wendet gleichzeitig den Scope-1- und den Scope-3.3-Koeffizienten an
3. Die Ergebnisse erscheinen separat in Ihrer Bilanz, korrekt nach Scope aufgeteilt

> Diese automatische Berechnung stellt sicher, dass Ihre Bilanz vollständig und GHG-Protocol-konform ist, ohne Risiko von Auslassungen oder Doppelzählungen.`
    }
  ],

  'products': [
    {
      id: 'pr-1',
      question_fr: 'Comment comptabiliser mes produits vendus ?',
      question_de: 'Wie erfasse ich meine verkauften Produkte?',
      answer_fr: `## Produits vendus — Scope 3 Aval

Les émissions liées aux produits vendus sont comptabilisées dans le **Scope 3 Aval** : elles couvrent l'utilisation, la transformation et la fin de vie des produits que vous mettez sur le marché. C'est souvent une part difficile à comptabiliser car elle dépend de la durée de vie du produit, de l'usage par le client et de son mode de traitement en fin de vie.

## Deux voies de saisie

CarbonScope propose deux approches complémentaires selon votre niveau de maturité et la nature de votre catalogue :

**Voie A — Fiches produits** : pour une comptabilité détaillée par produit avec analyse de cycle de vie complète (matières, fabrication, usage, fin de vie). Recommandée si vous avez un catalogue limité et bien défini, ou un besoin de reporting détaillé par produit.

**Voie B — Saisie directe** : pour une saisie rapide au niveau global, sans modéliser chaque produit individuellement. Recommandée si vous avez trop de références à modéliser, ou si vous souhaitez une première estimation rapide avant d'investir dans la modélisation détaillée.

> Les deux voies coexistent et peuvent être combinées dans un même exercice — par exemple, fiches détaillées pour vos 5 produits phares qui représentent 80% du volume, saisie directe pour le reste.

## Comment choisir ?

| Situation | Voie recommandée |
|-----------|-----------------|
| Catalogue limité et bien défini | Voie A |
| Besoin de reporting détaillé par produit | Voie A |
| Trop de références à modéliser | Voie B |
| Début de démarche, estimation rapide | Voie B |
| Produits non stratégiques en impact carbone | Voie B |`,
      answer_de: `## Verkaufte Produkte — Scope 3 nachgelagert

Die mit den verkauften Produkten verbundenen Emissionen werden unter **Scope 3 Downstream** erfasst: Sie umfassen die Nutzung, die Weiterverarbeitung und das Ende der Lebensdauer der Produkte, die Sie auf den Markt bringen. Dieser Teil ist oft schwer zu erfassen, da er von der Lebensdauer des Produkts, der Nutzung durch den Kunden und der Art der Entsorgung am Ende der Lebensdauer abhängt.

## Zwei Erfassungswege

CarbonScope bietet zwei komplementäre Ansätze je nach Ihrem Reifegrad und der Art Ihres Katalogs:

**Weg A — Produktkarten** : für eine detaillierte Bilanzierung pro Produkt mit vollständiger Lebenszyklusanalyse (Materialien, Herstellung, Nutzung, Entsorgung). Empfohlen bei einem begrenzten, klar definierten Katalog oder bei Bedarf an detailliertem Produktreporting.

**Weg B — Direkte Erfassung** : für eine schnelle Erfassung auf globaler Ebene, ohne jedes Produkt einzeln zu modellieren. Empfohlen bei zu vielen Referenzen zur Modellierung oder für eine erste schnelle Schätzung.

> Beide Wege können in einem Geschäftsjahr kombiniert werden — zum Beispiel detaillierte Karten für Ihre 5 Hauptprodukte, die 80% des Volumens ausmachen, direkte Erfassung für den Rest.

## Wie wählen?

| Situation | Empfohlener Weg |
|-----------|----------------|
| Begrenzter, klar definierter Katalog | Weg A |
| Bedarf an detailliertem Produktreporting | Weg A |
| Zu viele Referenzen zur Modellierung | Weg B |
| Beginn der Bilanzierung, schnelle Schätzung | Weg B |
| Nicht strategische Produkte nach CO₂-Impact | Weg B |`
    },
    {
      id: 'pr-2',
      question_fr: 'Comment créer une fiche produit ?',
      question_de: 'Wie erstelle ich eine Produktkarte?',
      answer_fr: `## Créer une fiche produit — Voie A

Accédez à la page **Produits** depuis le menu principal. Vous y trouverez votre bibliothèque de fiches produits. Cliquez sur **Nouveau produit** pour lancer le wizard de création en 5 étapes.

## Les 5 étapes du wizard

**1. Informations générales**
- Nom du produit et unité de vente
- Durée de vie du produit (en années) — utilisée pour calculer les émissions d'usage et de fin de vie

**2. Phase de transformation (fabrication)**
- Énergies et matières premières utilisées à la production
- Chaque élément est associé à son facteur d'émission

**3. Phase d'usage**
- Consommations énergétiques par cycle d'utilisation
- Nombre de cycles sur la durée de vie totale du produit

**4. Fin de vie**
- Modes de traitement des déchets : incinération, recyclage, mise en décharge…
- Chaque mode a son propre coefficient d'émission

**5. Résumé**
- Prévisualisation du profil d'émission total par unité de produit (kgCO₂e/unité)
- Ce profil sert de base à tous les calculs de ventes

## Enregistrement des ventes

Une fois la fiche créée, enregistrez le volume vendu sur l'exercice. CarbonScope génère alors automatiquement **trois activités** dans votre bilan :

- **Transformation produits** — émissions de fabrication × volume vendu
- **Utilisation produits** — émissions d'usage × volume vendu
- **Fin de vie produits** — émissions de fin de vie × volume vendu

> Vous n'avez pas à saisir ces trois activités manuellement — elles sont calculées et ventilées automatiquement à partir du profil produit et du volume vendu.`,
      answer_de: `## Produktkarte erstellen — Weg A

Navigieren Sie zur Seite **Produkte** im Hauptmenü. Dort finden Sie Ihre Produktkartenbibliothek. Klicken Sie auf **Neues Produkt**, um den 5-schrittigen Erstellungsassistenten zu starten.

## Die 5 Schritte des Assistenten

**1. Allgemeine Informationen**
- Produktname und Verkaufseinheit
- Produktlebensdauer (in Jahren) — zur Berechnung der Nutzungs- und Entsorgungsemissionen

**2. Transformationsphase (Herstellung)**
- Bei der Produktion verwendete Energien und Rohstoffe
- Jedes Element wird mit seinem Emissionsfaktor verknüpft

**3. Nutzungsphase**
- Energieverbrauch pro Nutzungszyklus
- Anzahl der Zyklen über die gesamte Produktlebensdauer

**4. Entsorgung**
- Abfallbehandlungsarten: Verbrennung, Recycling, Deponierung…
- Jede Art hat ihren eigenen Emissionskoeffizienten

**5. Zusammenfassung**
- Vorschau des gesamten Emissionsprofils pro Produkteinheit (kgCO₂e/Einheit)
- Dieses Profil dient als Grundlage für alle Verkaufsberechnungen

## Verkaufserfassung

Nach der Kartenerstellung erfassen Sie das verkaufte Volumen des Geschäftsjahres. CarbonScope generiert dann automatisch **drei Aktivitäten** in Ihrer Bilanz:

- **Produkttransformation** — Herstellungsemissionen × verkauftes Volumen
- **Produktnutzung** — Nutzungsemissionen × verkauftes Volumen
- **Produktentsorgung** — Entsorgungsemissionen × verkauftes Volumen

> Sie müssen diese drei Aktivitäten nicht manuell erfassen — sie werden automatisch aus dem Produktprofil und dem verkauften Volumen berechnet und aufgeteilt.`
    },
    {
      id: 'pr-3',
      question_fr: "Qu'est-ce que les profils d'émission par exercice ?",
      question_de: 'Was sind Emissionsprofile pro Geschäftsjahr?',
      answer_fr: `## Profils d'émission par exercice

Chaque fiche produit peut avoir un **profil d'émission différent selon l'exercice fiscal**. Concrètement, cela signifie que les caractéristiques d'un produit — ses matières premières, ses fournisseurs, son processus de fabrication — peuvent évoluer d'une année à l'autre sans perdre l'historique.

## Pourquoi c'est important

Imaginez que vous passiez en 2026 à un fournisseur d'acier plus vertueux, réduisant l'empreinte de fabrication de votre produit de 30%. Sans profils par exercice, vous seriez contraints de modifier la fiche produit et de perdre la cohérence de vos données historiques.

Avec les profils par exercice :

- Les ventes 2024 conservent le profil 2024 — l'historique reste intact
- Les ventes 2026 utilisent le nouveau profil 2026 — le progrès est reflété
- La comparaison N-1 est fiable et auditables

## Traçabilité et conformité

Les profils passés sont **figés automatiquement** une fois l'exercice clôturé. Cela garantit qu'aucune modification ultérieure ne vienne altérer rétroactivement vos données — une exigence clé pour tout reporting carbone auditable.

> Ce mécanisme est particulièrement utile pour les entreprises qui travaillent activement à l'amélioration de leur chaîne d'approvisionnement et souhaitent en documenter les progrès de manière rigoureuse.`,
      answer_de: `## Emissionsprofile pro Geschäftsjahr

Jede Produktkarte kann ein **unterschiedliches Emissionsprofil je nach Geschäftsjahr** haben. Konkret bedeutet dies, dass die Eigenschaften eines Produkts — seine Rohstoffe, Lieferanten, Herstellungsprozess — sich von Jahr zu Jahr ändern können, ohne die Historie zu verlieren.

## Warum das wichtig ist

Stellen Sie sich vor, Sie wechseln 2026 zu einem nachhaltigeren Stahllieferanten und reduzieren den Herstellungs-Fußabdruck Ihres Produkts um 30%. Ohne Profile pro Geschäftsjahr müssten Sie die Produktkarte ändern und die Konsistenz Ihrer historischen Daten verlieren.

Mit Profilen pro Geschäftsjahr:

- Verkäufe 2024 behalten das Profil 2024 — die Historie bleibt intakt
- Verkäufe 2026 verwenden das neue Profil 2026 — der Fortschritt wird abgebildet
- Der N-1-Vergleich ist zuverlässig und prüfbar

## Rückverfolgbarkeit und Konformität

Vergangene Profile werden **automatisch eingefroren**, sobald das Geschäftsjahr abgeschlossen ist. Dies stellt sicher, dass keine nachträglichen Änderungen Ihre Daten rückwirkend verfälschen — eine Schlüsselanforderung für jedes prüffähige CO₂-Reporting.

> Dieser Mechanismus ist besonders nützlich für Unternehmen, die aktiv an der Verbesserung ihrer Lieferkette arbeiten und ihre Fortschritte rigoros dokumentieren möchten.`
    }
  ]
};
